// apps/web/src/auth/SignInScreen.tsx
// GIS Sign-in composite — renders the Google-styled "Sign in with Google"
// button, fires postCredential() on successful GIS callback, redirects to
// `/` (TOC landing) on success or shows error inline.
//
// Per Plan §4.7.4 architecture + Plan §4.7.10 carry-forward #1
// (<SignInScreen/> composite). Sign-in destination flipped from `/brief`
// to `/` at Option D #3 (2026-05-21) — TOC landing now exists.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { postCredential, type CredentialResponse } from './gis-client.js';

export function SignInScreen() {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError]     = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate              = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const onCredential = async (response: CredentialResponse) => {
      setError(null);
      try {
        await postCredential(response.credential);
        await navigate({ to: '/' });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    };

    const tryInit = () => {
      if (cancelled) return;
      const gsi = window.google?.accounts?.id;
      if (!gsi) {
        // Script still loading — poll every 100ms (typical load 100-300ms).
        setTimeout(tryInit, 100);
        return;
      }

      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!clientId) {
        setError('VITE_GOOGLE_CLIENT_ID is not configured — check .env.local');
        setLoading(false);
        return;
      }

      gsi.initialize({
        client_id: clientId,
        callback:  onCredential,
      });

      if (buttonRef.current) {
        gsi.renderButton(buttonRef.current, {
          type:  'standard',
          theme: 'filled_black',
          size:  'large',
          text:  'signin_with',
        });
      }

      setLoading(false);
    };

    tryInit();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <main
      style={{
        padding:    '4rem 2rem',
        maxWidth:   400,
        margin:     '0 auto',
        textAlign:  'center',
      }}
    >
      <h1 style={{ marginBottom: '2rem', color: 'var(--color-text-1)' }}>
        Sign in to cf-apps
      </h1>

      {loading && (
        <p style={{ color: 'var(--color-text-3)' }}>Loading sign-in…</p>
      )}

      <div ref={buttonRef} style={{ display: 'inline-block' }} />

      {error && (
        <p
          style={{
            color:       'var(--color-fail)',
            marginTop:   '1rem',
            fontSize:    '0.9rem',
          }}
        >
          {error}
        </p>
      )}
    </main>
  );
}
